/* External */
import {
  memo, useMemo, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';

/* Components */
import MiniScheduleCard from './MiniScheduleCard';
import ScrollIntoViewIfFocused from '../../ScrollIntoViewIfFocused';

/* Providers */
import { useUserAccount } from '../../../providers/UserProvider';

/* Hooks */
import useContent from '../../../hooks/useContent';
import useFetchClickAction from '../../../hooks/useFetchClickAction';

/* Utils */
import useCurrentAndNextSchedulesAndFetch from '../../../hooks/useCurrentAndNextSchedulesAndFetch';
import { redirectToPlayer } from '../../../utils/history';
import { ScrollPosition } from '../../ScrollView';
import { useItemData } from '../../VirtualizedList';
import { COMPONENTS } from '../../../utils/focusKey';
import {
  PLAY_SOURCE_PAGE,
  PLAY_SOURCE_SECTION,
} from '../../../utils/trackSourceConviva';
import { Direction } from '../../../utils/spatialNavigation';
import { formatPlaySource, GTM_EVENT_NAMES } from '../../../utils/googleTags';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';
import { getCurrentUrlOfPlayerContent } from '../utils';
import { IS_TAPPP_SDK_ACTIVE } from '../../../utils/env';
import { appConsoleLog } from '../../../utils/functions';

function MiniEpgChannelLane({
  index,
  firstVisibleElementIndex,
}) {
  const { gtmState } = useGtmLocalContext();
  const {
    channels,
    handleChannelArrowPress,
    currentChannelId,
    handleScheduleBecameFocused,
    handleScheduleMouseEnter,
    homeOnBack,
    focusedScheduleToUse,
    setFocusedSchedule,
    miniEpgCommonTagEvent,
  } = useItemData();

  const {
    msoProvider,
    accountType: userType,
  } = useUserAccount() || {};

  const { fetchClickAction } = useFetchClickAction();

  const channel = useMemo(
    () => channels.get(index),
    [index, channels],
  );

  const { currentAndNextSchedules, isFetching } = useCurrentAndNextSchedulesAndFetch(channel, 1);

  const schedule = currentAndNextSchedules?.get(0);
  const currentLiveContent = useContent(schedule?.content);

  const locationSectionName = gtmState?.miniEpgLiveTitle === 'for-you' ? 'recent' : 'allchannels';

  const playSource = formatPlaySource({
    parentLander: 'miniepg',
    section: locationSectionName,
  });

  const locationState = useCallback(() => {
    const currentURL = getCurrentUrlOfPlayerContent();
    return {
      actionOrigin: currentLiveContent?.actionOrigin || '',
      transmissionType: channel?.schema,
      sectionName: locationSectionName,
      componentType: 'miniepg',
      lastContentUrl: currentURL,
      gtmPlaySource: playSource,
    };
  }, [channel?.schema, locationSectionName, playSource, currentLiveContent]);

  const contentListPosition = useMemo(() => index + 1, [index]);

  const handleEpgWatchChannelEvent = useCallback(() => {
    miniEpgCommonTagEvent({
      eventName: GTM_EVENT_NAMES.EPG.WATCH_CHANNEL,
      extraData: {
        newContent: currentLiveContent,
        newChannelId: channel?.channelId,
        newChannelName: channel?.title,
        startTime: schedule?.startTime,
        endTime: schedule?.endTime,
        listPosition: contentListPosition,
      },
    });
  }, [
    channel?.channelId,
    channel?.title,
    currentLiveContent,
    contentListPosition,
    miniEpgCommonTagEvent,
    schedule?.endTime,
    schedule?.startTime,
  ]);

  const clickActionParams = useMemo(() => ({
    actionOrigin: currentLiveContent?.actionOrigin || '',
    userOrigin: {
      playSource: PLAY_SOURCE_PAGE.PLAYER,
      userLogin: userType || '',
      msoProvider: msoProvider || '',
    },
    contentType: currentLiveContent?.isLive ? 1 : 2,
    guideId: currentLiveContent?.streaming?.id || currentLiveContent?.vrioAssetId || '',
    vrioAssetId: currentLiveContent?.vrioAssetId || '',
  }), [currentLiveContent, userType, msoProvider]);

  const handleRedirectToPlayer = useCallback(() => {
    if (!schedule?.blackout && currentChannelId !== channel.channelId) {
      if (IS_TAPPP_SDK_ACTIVE && window.TapppPanelLibrary) {
        try {
          window.TapppPanelLibrary?.destroy();

          appConsoleLog('[TAPPP] MiniEPG: Called Destroy TapppPanel');
        } catch (error) {
          console.error('[TAPPP] Error on MiniEPG call destroy TapppPanel', error);
        }
      }
      setTimeout(() => {
        handleEpgWatchChannelEvent();
        fetchClickAction(clickActionParams);
        redirectToPlayer(channel, {
          replace: true,
          page: PLAY_SOURCE_PAGE.PLAYER,
          section: PLAY_SOURCE_SECTION.LIVE_MINI_EPG,
          query: homeOnBack ? { homeOnBack: true } : undefined,
          state: locationState(),
        });
      }, 1000);
    }
  }, [
    schedule?.blackout,
    currentChannelId,
    channel,
    handleEpgWatchChannelEvent,
    homeOnBack,
    locationState,
    clickActionParams,
    fetchClickAction,
  ]);

  const handleOnArrowPress = useCallback((direction, ...props) => {
    if ((index < firstVisibleElementIndex) && direction === Direction.UP) {
      return false;
    }
    return handleChannelArrowPress(direction, { index, ...props });
  }, [handleChannelArrowPress, index, firstVisibleElementIndex]);

  useEffect(() => {
    if (focusedScheduleToUse?.channelId === channel?.channelId) {
      setFocusedSchedule(schedule);
    }
  }, [focusedScheduleToUse?.channelId, schedule, setFocusedSchedule, channel?.channelId]);

  useEffect(() => {
    if (index === 0) {
      setFocusedSchedule(undefined);
    }
  }, [index, setFocusedSchedule]);

  return (
    <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
      <MiniScheduleCard
        schedule={schedule}
        isFetching={isFetching}
        onEnterPress={handleRedirectToPlayer}
        onArrowPress={handleOnArrowPress}
        channel={channel}
        currentChannelId={currentChannelId}
        focusKey={`${COMPONENTS.MINI_EPG_CARD}${index}`}
        onBecameFocused={handleScheduleBecameFocused}
        onMouseEnter={handleScheduleMouseEnter}
      />
    </ScrollIntoViewIfFocused>
  );
}

MiniEpgChannelLane.propTypes = {
  index: PropTypes.number.isRequired,
  firstVisibleElementIndex: PropTypes.number.isRequired,
};

export default memo(MiniEpgChannelLane);
